/**
 * offlineManager.js
 * Maneja el caché de datos y la cola de pedidos para uso sin conexión.
 * La cola de pedidos y todos los datos sincronizados usan IndexedDB como
 * fuente de verdad. localStorage queda reservado para preferencias y marcas
 * de tiempo livianas.
 */

import CatalogStorage from './catalogStorage'
import ReferenceStorage from './referenceStorage'

const PREFIX = 'offl_'
const DB_NAME = 'mobile_orders_offline_db'
const DB_VERSION = 1
const STORE_QUEUE = 'orders_queue'

const REFERENCE_BLOCKS = Object.freeze({
  ZONES: 'zones',
  PRICE_LISTS: 'price_lists',
  UNIT_MEASUREMENTS: 'unit_measurements',
  POINTS_SALES: 'points_sales',
  METHODS_PAYMENTS: 'methods_payments',
  IVA_CONDITIONS: 'iva_conditions',
  CONFIGURATIONS: 'configurations',
  REASONS: 'reasons',
})

let queueMemory = null
let dbPromise = null

function safeGet(key) {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al guardar en localStorage:', key, e)
    return false
  }
}

function setQueueMemory(queue) {
  queueMemory = Array.isArray(queue) ? queue : []
  return queueMemory
}

function openQueueDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: 'localId' })
      }
    }

    request.onsuccess = event => resolve(event.target.result)
    request.onerror = () => reject(request.error || new Error('No se pudo abrir IndexedDB'))
  })

  return dbPromise
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Operación IndexedDB fallida'))
  })
}

async function loadQueueFromIndexedDb() {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readonly').objectStore(STORE_QUEUE)
    const items = await requestToPromise(store.getAll())
    return (items || []).sort((a, b) => String(a.savedAt || '').localeCompare(String(b.savedAt || '')))
  } catch (e) {
    console.warn('[OfflineManager] Error al leer cola desde IndexedDB:', e)
    return []
  }
}

async function saveQueueItemToIndexedDb(item) {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readwrite').objectStore(STORE_QUEUE)
    await requestToPromise(store.put(item))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al guardar item de cola en IndexedDB:', e)
    return false
  }
}

async function deleteQueueItemFromIndexedDb(localId) {
  try {
    const db = await openQueueDb()
    const store = db.transaction(STORE_QUEUE, 'readwrite').objectStore(STORE_QUEUE)
    await requestToPromise(store.delete(localId))
    return true
  } catch (e) {
    console.warn('[OfflineManager] Error al borrar item de cola en IndexedDB:', e)
    return false
  }
}

// Si la app se cerró/crasheó a mitad de un envío, el item queda en 'syncing'
// para siempre y desaparece de los contadores. Se recupera como 'pending'.
function recoverStuckItems(queue) {
  let recovered = false
  const fixed = queue.map(item => {
    if (item.status !== 'syncing') return item
    recovered = true
    return {
      ...item,
      status: 'pending',
      error: item.error || 'Se recuperó un envío interrumpido',
    }
  })
  if (recovered) {
    fixed.filter(i => i.error === 'Se recuperó un envío interrumpido').forEach(i => saveQueueItemToIndexedDb(i))
  }
  return fixed
}

const OfflineManager = {

  // ─────────────────────────────────────────────
  // Zonas + clientes del vendedor
  // ─────────────────────────────────────────────
  async saveZones(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.ZONES, data)
  },
  async getZones(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.ZONES)
  },

  // ─────────────────────────────────────────────
  // Catálogo de productos
  // ─────────────────────────────────────────────
  async saveProducts(sellerId, data) {
    return CatalogStorage.replaceProducts(sellerId, data)
  },
  async getProducts(sellerId) {
    return CatalogStorage.getAllProducts(sellerId)
  },
  async getProduct(sellerId, productId) {
    return CatalogStorage.getProduct(sellerId, productId)
  },
  async searchProducts(sellerId, query, options = {}) {
    return CatalogStorage.searchProducts(sellerId, query, options)
  },
  async hasProducts(sellerId) {
    return CatalogStorage.hasProducts(sellerId)
  },

  // ─────────────────────────────────────────────
  // Listas de precios
  // ─────────────────────────────────────────────
  async savePriceLists(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.PRICE_LISTS, data)
  },
  async getPriceLists(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.PRICE_LISTS)
  },

  // ─────────────────────────────────────────────
  // Unidades de medida
  // ─────────────────────────────────────────────
  async saveUnitMeasurements(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.UNIT_MEASUREMENTS, data)
  },
  async getUnitMeasurements(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.UNIT_MEASUREMENTS)
  },

  // ─────────────────────────────────────────────
  // Puntos de venta
  // ─────────────────────────────────────────────
  async savePointsSales(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.POINTS_SALES, data)
  },
  async getPointsSales(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.POINTS_SALES)
  },

  // ─────────────────────────────────────────────
  // Medios de pago
  // ─────────────────────────────────────────────
  async saveMethodsPayments(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.METHODS_PAYMENTS, data)
  },
  async getMethodsPayments(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.METHODS_PAYMENTS)
  },

  // ─────────────────────────────────────────────
  // Condiciones de IVA (para calcular precio c/IVA sin conexión)
  // ─────────────────────────────────────────────
  async saveIvaConditions(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.IVA_CONDITIONS, data)
  },
  async getIvaConditions(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.IVA_CONDITIONS)
  },

  // ─────────────────────────────────────────────
  // Configuración del módulo
  // ─────────────────────────────────────────────
  async saveConfigurations(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.CONFIGURATIONS, data)
  },
  async getConfigurations(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.CONFIGURATIONS)
  },

  // ─────────────────────────────────────────────
  // Timestamp del último sync
  // ─────────────────────────────────────────────
  setLastSync(sellerId) {
    return safeSet(`${PREFIX}last_sync_${sellerId}`, Date.now())
  },
  getLastSync(sellerId) {
    return safeGet(`${PREFIX}last_sync_${sellerId}`)
  },
  formatLastSync(sellerId) {
    const ts = this.getLastSync(sellerId)
    if (!ts) return null
    const d = new Date(ts)
    return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
  },

  // ─────────────────────────────────────────────
  // Preferencia de uso local (independiente por vendedor)
  // ─────────────────────────────────────────────
  setLocalFirstMode(sellerId, enabled) {
    return safeSet(`${PREFIX}local_first_${sellerId}`, !!enabled)
  },
  getLocalFirstMode(sellerId) {
    return safeGet(`${PREFIX}local_first_${sellerId}`) === true
  },

  // ─────────────────────────────────────────────
  // Motivos de pedido
  // ─────────────────────────────────────────────
  async saveReasons(sellerId, data) {
    return ReferenceStorage.replaceBlock(sellerId, REFERENCE_BLOCKS.REASONS, data)
  },
  async getReasons(sellerId) {
    return ReferenceStorage.getBlock(sellerId, REFERENCE_BLOCKS.REASONS)
  },

  // ─────────────────────────────────────────────
  // Cola de pedidos offline (IndexedDB como fuente de verdad)
  // ─────────────────────────────────────────────
  getQueue() {
    return queueMemory !== null ? queueMemory : setQueueMemory([])
  },

  // Se llama al montar/activar la pantalla: carga la cola desde IndexedDB y
  // recupera items trabados en 'syncing' por un cierre abrupto de la app.
  async restoreQueueFromBackup() {
    const queue = recoverStuckItems(await loadQueueFromIndexedDb())

    setQueueMemory(queue)
    return queue
  },

  async addToQueue(orderData, isEdit = false, extras = {}) {
    const queue = this.getQueue()
    const localId = `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    const item = {
      localId,
      savedAt: new Date().toISOString(),
      status: 'pending',   // pending | syncing | error
      error: null,
      attempts: 0,
      isEdit,
      // resaved: el alta viene de corregir un item que ya había intentado
      // sincronizarse; si el servidor responde idempotency_hit, las
      // correcciones deben aplicarse como edición sobre el pedido existente.
      resaved: !!extras.resaved,
      data: orderData,
    }

    const saved = await saveQueueItemToIndexedDb(item)
    if (!saved) return null

    setQueueMemory(queue.concat([item]))
    return localId
  },

  updateQueueItem(localId, updates) {
    const queue = this.getQueue()
    const idx = queue.findIndex(i => i.localId === localId)
    if (idx !== -1) {
      queue[idx] = { ...queue[idx], ...updates }
      setQueueMemory(queue)
      saveQueueItemToIndexedDb(queue[idx])
    }
  },

  removeFromQueue(localId) {
    const queue = this.getQueue().filter(i => i.localId !== localId)
    setQueueMemory(queue)
    deleteQueueItemFromIndexedDb(localId)
  },

  // Un pedido "en curso" (syncing) también cuenta como pendiente: nunca debe
  // desaparecer del contador mientras no haya confirmación del servidor.
  pendingCount() {
    return this.getQueue().filter(i => i.status === 'pending' || i.status === 'error' || i.status === 'syncing').length
  },

}

export default OfflineManager
